import { css, jsx } from '@emotion/core';
import { Col, Form as AntForm, Icon, Input, Modal, notification, Radio, Row, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uid } from 'uid';

import {
  deleteCompliassistSavedSearch,
  requestSavedSearches,
  requestSearchOptions,
  saveCompliassistSearch,
} from '@/src/compliance/redux/complianceReducer';
import { areAllFieldsEmpty } from '@/src/search/utils';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import X from '@/src/shared/components/Icon/_icons/X';
import { bp, space } from '@/styles';
import { addTransformUtils, getArrValues } from '@/utils/formHelpers';
import { colMdProps, rowProps } from '@/utils/rowColHelpers';
import { multiSelectProps, singleSelectProps } from '@/utils/selectHelpers';

import style from './style';

const { Option } = Select;

const { m } = space;
const EditSavedSearch = props => {
  const { visible, closeModal, form, data, setRefreshState } = props;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(requestSearchOptions());
  }, []);

  const { searchOptions } = useSelector(state => state.compliance);
  const personId = useSelector(state => state.auth.userinfo.PersonID);

  const {
    isLoadingOptions,
    FieldOptions: { Charts, Categories, States },
  } = searchOptions;

  if (!visible) {
    return null;
  }

  const handleSave = async () => {
    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) {
        return;
      }

      if (areAllFieldsEmpty(form)) {
        notification.error({
          message: 'Search',
          description: 'Search option is required.',
        });
        return;
      }

      const _values = { ...values };
      const copyFieldOptions = addTransformUtils(_values.FieldOptions, searchOptions.FieldOptions);

      const payload = {
        ...data,
        ..._values,
        FieldOptions: {
          ...data.FieldOptions,
          SearchText: _values.FieldOptions.SearchText,
          States: copyFieldOptions.getFullComplex('States', 'StateCode'),
          Charts: copyFieldOptions.getFullComplex('Charts', 'ChartId'),
          Categories: _values.FieldOptions.Category
            ? [{ CategoryId: _values.FieldOptions.Category }]
            : [],
        },
      };
      dispatch(saveCompliassistSearch({ payload, successCallback: closeModal }));
    });
  };

  const handleDelete = () => {
    dispatch(deleteCompliassistSavedSearch(data.SearchId));
    dispatch(requestSavedSearches({ personId }));
    setRefreshState(uid());
  };

  const renderMultiSelect = ({
    options,
    optCode,
    optDescription,
    label,
    _props = {},
    initialValue = [],
    field,
  }) => {
    const { getFieldDecorator } = form;

    return (
      <Col {...colMdProps}>
        <AntForm.Item label={label}>
          {getFieldDecorator(field, {
            initialValue: getArrValues(initialValue, optCode),
          })(
            <Select {...multiSelectProps(isLoadingOptions)} {..._props}>
              {options.map(opt => (
                <Option value={opt[optCode]} key={`${opt[optDescription]}-${label}`}>
                  {opt[optDescription]}
                </Option>
              ))}
            </Select>
          )}
        </AntForm.Item>
      </Col>
    );
  };

  const { Category } = form.getFieldValue('FieldOptions') || {};
  const filteredCharts = Charts.filter(chart => {
    return !Category || chart.Categories.find(({ CategoryId }) => CategoryId === Category);
  });

  return (
    <Modal
      visible={visible}
      onCancel={closeModal}
      width="80%"
      footer={
        isLoadingOptions
          ? null
          : [
              <div css={style.footerWrapper} key="delete-button">
                <span css={style.footerButtonWrapper}>
                  <Button size="small" theme="red" onClick={handleDelete}>
                    <Icon type="delete" />
                    Delete Saved Search
                  </Button>
                </span>
                <span css={style.footerButtonWrapper}>
                  <Button
                    theme="gray"
                    size="small"
                    css={style.cancel}
                    disabled={false}
                    onClick={closeModal}
                  >
                    <span css={style.cancelInner}>
                      <span css={style.cancelIcon}>
                        <X css={style.cancelIcon} />
                      </span>
                      <span css={style.cancelIcon}>Cancel</span>
                    </span>
                  </Button>
                  <Button
                    disabled={false}
                    size="small"
                    theme="default"
                    css={style.saveSearch}
                    htmlType="submit"
                    onClick={handleSave}
                  >
                    <Icon type="save" theme="filled" />
                    Save Search
                  </Button>
                </span>
              </div>,
            ]
      }
      maskStyle={{
        backgroundColor: 'rgba(0,38,73,0.65)',
      }}
      title={
        <Heading type={3} weight="bolder" css={m.b0} theme="gray">
          Edit Search
        </Heading>
      }
    >
      <Form>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Name
            </Heading>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Name of Search">
              {form.getFieldDecorator('SearchDescription', {
                initialValue: data.SearchDescription,
                rules: [
                  {
                    message: 'Enter Name of Search.',
                    required: true,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>
          <Col {...colMdProps}>
            <AntForm.Item label="Show on homepage">
              {form.getFieldDecorator('IncludeOnHome', {
                initialValue: data.IncludeOnHome,
              })(
                <Radio.Group>
                  <Radio value="Y">Yes</Radio>
                  <Radio value="N">No</Radio>
                </Radio.Group>
              )}
            </AntForm.Item>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Filters
            </Heading>
          </Col>
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Search Text">
              {form.getFieldDecorator('FieldOptions.SearchText', {
                initialValue: data.FieldOptions.SearchText,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>
          {renderMultiSelect({
            label: 'State',
            options: States,
            optCode: 'StateCode',
            optDescription: 'StateDescription',
            field: 'FieldOptions.States',
            initialValue: data.FieldOptions.States,
          })}
        </Row>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Category">
              {form.getFieldDecorator('FieldOptions.Category', {
                initialValue: data.FieldOptions.Categories.length
                  ? data.FieldOptions.Categories[0].CategoryId
                  : undefined,
              })(
                <Select {...singleSelectProps(isLoadingOptions)}>
                  {Categories.map(category => (
                    <Option value={category.CategoryId} key={`${category.CategoryId}-category`}>
                      {category.CategoryDescription}
                    </Option>
                  ))}
                </Select>
              )}
            </AntForm.Item>
          </Col>
          {renderMultiSelect({
            label: 'Charts',
            options: filteredCharts,
            optCode: 'ChartId',
            optDescription: 'ChartTitle',
            field: 'FieldOptions.Charts',
            initialValue: data.FieldOptions.Charts,
          })}
        </Row>
      </Form>
    </Modal>
  );
};

EditSavedSearch.propTypes = {
  form: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

export default AntForm.create()(EditSavedSearch);
